// pages/RecoverPasswordPage.tsx
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../styles/auth-page.css";
import { BASE_URLS } from "../config/urls";
import {BtnSpinner} from "../components/fields/BtnSpinner";

type Step = 1 | 2 | 3;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const passwordLengthRegex = /^.{6,}$/;
const passwordLettersRegex = /[A-Za-z]/;

function isValidEmail(email: string) {
    return emailRegex.test(email.trim().toLowerCase());
}

function isValidPassword(pw: string) {
    return passwordLengthRegex.test(pw) && passwordLettersRegex.test(pw);
}

export default function RecoverPasswordPage() {
    const [step, setStep] = useState<Step>(1);
    const [loading, setLoading] = useState(false);

    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");

    const [emailError, setEmailError] = useState("");
    const [codeError, setCodeError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [formError, setFormError] = useState("");

    const [showPass, setShowPass] = useState(false);

    const title = useMemo(() => {
        if (step === 1) return "Восстановление пароля";
        if (step === 2) return "Проверка кода";
        return "Новый пароль";
    }, [step]);

    const resetErrors = () => {
        setEmailError("");
        setCodeError("");
        setPasswordError("");
        setFormError("");
    };

    const requestRecover = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        resetErrors();

        const normalizedEmail = email.trim().toLowerCase();
        if (!isValidEmail(normalizedEmail)) {
            setEmailError("Неправильная электронная почта");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${BASE_URLS.api}/recover_password`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json" },
                body: JSON.stringify({ email: normalizedEmail }),
            });

            let data: any = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            if (!res.ok) {
                setEmailError(data?.error ?? data?.message ?? "Ошибка восстановления пароля");
                return;
            }

            setStep(2);
        } catch (err: any) {
            console.error(err);
            setFormError("Не удалось выполнить запрос. Попробуйте позже.");
        } finally {
            setLoading(false);
        }
    };

    const checkCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        resetErrors();

        const normalizedEmail = email.trim().toLowerCase();
        const normalizedCode = code.trim();

        if (!normalizedCode) {
            setCodeError("Введите проверочный код");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${BASE_URLS.api}/recover_password/check`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json" },
                body: JSON.stringify({ email: normalizedEmail, code: normalizedCode }),
            });

            let data: any = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            if (!res.ok) {
                setCodeError(data?.error ?? data?.message ?? "Неверный временный код");
                return;
            }

            setStep(3);
        } catch (err: any) {
            console.error(err);
            setFormError("Не удалось выполнить запрос. Попробуйте позже.");
        } finally {
            setLoading(false);
        }
    };

    const setPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        resetErrors();

        const normalizedEmail = email.trim().toLowerCase();
        const normalizedCode = code.trim();

        if (!isValidPassword(newPassword)) {
            setPasswordError("Пароль должен быть от 6 символов и содержать буквы латинского алфавита");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${BASE_URLS.api}/recover_password/new_password`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json" },
                body: JSON.stringify({ email: normalizedEmail, code: normalizedCode, password: newPassword }),
            });

            let data: any = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            if (!res.ok) {
                setPasswordError(data?.error ?? data?.message ?? "Не удалось обновить пароль");
                return;
            }

            window.location.assign(`${BASE_URLS.app}/login`);
        } catch (err: any) {
            console.error(err);
            setFormError("Не удалось выполнить запрос. Попробуйте позже.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <HelmetProvider>
                <Helmet title="Восстановление пароля" />
            </HelmetProvider>

            <div className="auth-wrap">
                <div className="auth-card card" style={{ maxWidth: 440 }}>
                    <div className="auth-head">
                        <h1 className="auth-title">{title}</h1>
                    </div>

                    {formError && <div className="auth-alert auth-alert--error">{formError}</div>}

                    {step === 1 && (
                        <>
                            <div className="auth-sub" style={{ marginTop: 4 }}>
                                Введите email, который вы использовали при регистрации
                            </div>

                            <form className="auth-form" onSubmit={requestRecover}>
                                <fieldset disabled={loading} style={{ border: "none", padding: 0, margin: 0 }}>
                                    <div className="auth-field" style={{margin: "20px 0"}}>
                                        <div className="auth-label">Email</div>
                                        <input
                                            className={`auth-input ${emailError ? "is-error" : ""}`}
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                setEmailError("");
                                                setFormError("");
                                            }}
                                            placeholder="name@mail.com"
                                            inputMode="email"
                                            autoComplete="email"
                                        />
                                        {emailError && <div className="auth-error">{emailError}</div>}
                                    </div>

                                    <button className="auth-submit btn-primary" type="submit" disabled={loading}>
                                        {loading ? <BtnSpinner/> : "Восстановить"}
                                    </button>
                                </fieldset>
                            </form>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="auth-sub" style={{ marginTop: 4 }}>
                                Введите проверочный код, который был направлен вам на почту
                            </div>

                            <form className="auth-form" onSubmit={checkCode}>
                                <fieldset disabled={loading} style={{ border: "none", padding: 0, margin: 0 }}>
                                    <div className="auth-field" style={{margin: "20px 0"}}>
                                        <div className="auth-label">Проверочный код</div>
                                        <input
                                            className={`auth-input ${codeError ? "is-error" : ""}`}
                                            value={code}
                                            onChange={(e) => {
                                                setCode(e.target.value);
                                                setCodeError("");
                                                setFormError("");
                                            }}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                        />
                                        {codeError && <div className="auth-error">{codeError}</div>}
                                    </div>

                                    <button className="auth-submit btn-primary" type="submit" disabled={loading}>
                                        {loading ? <BtnSpinner/> : "Проверить"}
                                    </button>

                                    <div className="auth-foot" style={{ marginTop: 10 }}>
                    <span className="auth-link" role="button" tabIndex={0} onClick={() => setStep(1)}>
                      Изменить email
                    </span>
                                    </div>
                                </fieldset>
                            </form>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <div className="auth-sub" style={{ marginTop: 4 }}>
                                Придумайте новый пароль
                            </div>

                            <form className="auth-form" onSubmit={setPassword}>
                                <fieldset disabled={loading} style={{ border: "none", padding: 0, margin: 0 }}>
                                    <div className="auth-field" style={{margin: "20px 0"}}>
                                        <div className="auth-label">Новый пароль</div>

                                        <div className="auth-pass-row">
                                            <input
                                                className={`auth-input auth-input--pass ${passwordError ? "is-error" : ""}`}
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    setPasswordError("");
                                                    setFormError("");
                                                }}
                                                placeholder="Минимум 6 символов"
                                                type={showPass ? "text" : "password"}
                                                autoComplete="new-password"
                                            />

                                            <button
                                                type="button"
                                                className="auth-eye"
                                                onClick={() => setShowPass((p) => !p)}
                                                aria-label={showPass ? "Скрыть пароль" : "Показать пароль"}
                                                title={showPass ? "Скрыть" : "Показать"}
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden
                                                >
                                                    {showPass ? (
                                                        <>
                                                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12" stroke="#000000"
                                                                  stroke-linecap="round"
                                                                  stroke-linejoin="round"></path>
                                                            <path d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12" stroke="#000000"
                                                                  stroke-linecap="round"
                                                                  stroke-linejoin="round"></path>
                                                            <circle cx="12" cy="12" r="3" stroke="#000000"
                                                                    stroke-linecap="round" stroke-linejoin="round"></circle>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <path d="M2 2L22 22" stroke="#000000" stroke-width="1"
                                                                  stroke-linecap="round" stroke-linejoin="round"></path>
                                                            <path
                                                                d="M6.71277 6.7226C3.66479 8.79527 2 12 2 12C2 12 5.63636 19 12 19C14.0503 19 15.8174 18.2734 17.2711 17.2884M11 5.05822C11.3254 5.02013 11.6588 5 12 5C18.3636 5 22 12 22 12C22 12 21.3082 13.3317 20 14.8335"
                                                                stroke="#000000" stroke-width="1" stroke-linecap="round"
                                                                stroke-linejoin="round"></path>
                                                            <path
                                                                d="M14 14.2362C13.4692 14.7112 12.7684 15.0001 12 15.0001C10.3431 15.0001 9 13.657 9 12.0001C9 11.1764 9.33193 10.4303 9.86932 9.88818"
                                                                stroke="#000000" stroke-width="1" stroke-linecap="round"
                                                                stroke-linejoin="round"></path>
                                                        </>
                                                    )}
                                                </svg>
                                            </button>
                                        </div>

                                        {passwordError && <div className="auth-error">{passwordError}</div>}
                                    </div>

                                    <button className="auth-submit btn-primary" type="submit" disabled={loading}>
                                        {loading ? <BtnSpinner/> : "Сохранить пароль"}
                                    </button>
                                </fieldset>
                            </form>
                        </>
                    )}

                    <div className="auth-foot" style={{ marginTop: 20 }}>
                        <div className="auth-row-between" style={{padding: "0 30px"}}>
                            <Link to="/login" className="auth-link">
                                Войти
                            </Link>
                            <Link to="/registration" className="auth-link">
                                Регистрация
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
